/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/navigation/NavigationItem.tsx
 * PURPOSE: Reusable navigation link item với active state và badge support.
 *          2026-08-15: web-sync checklist A.2 — `bottomTab` variant now
 *          renders a raised floating circle when `item.isCenter` is set
 *          (mirrors mobile's center "Chấm công" tab treatment).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance) + P6 (Component Library)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: UI Components, Vietnamese F&B User Experience
 * - Impact: User Experience, Interface Quality, Accessibility
 * - Stakeholders: End Users, UI/UX Team, Development Team
 *
 * @technical-architecture
 * - Layer: Presentation Layer, UI Components
 * - Pattern: React Component, Design System, CSS Variables
 * - Dependencies: React 18, TypeScript, CSS Variables (design-system.css)
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/LAYOUT-NAVIGATION-DESIGN.md
 * - Mockup: docs/02-design/04-ui-ux-design/NAVIGATION-FLOW-MOCKUP.html (lines 370-413)
 * - CSS: frontend/src/styles/design-system.css
 */

import React, { useState } from 'react';
import { NavigationItem } from '@/types/navigation';

interface NavigationItemProps {
  item: NavigationItem;
  isActive: boolean;
  isCollapsed?: boolean;
  onClick: () => void;
  variant?: 'sidebar' | 'bottomTab';
  showTooltip?: boolean;
}

export const NavigationItemComponent: React.FC<NavigationItemProps> = ({
  item,
  isActive,
  isCollapsed = false,
  onClick,
  variant = 'sidebar',
  showTooltip = true
}) => {
  const IconComponent = item.icon;
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!item.disabled) {
      onClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!item.disabled) {
        onClick();
      }
    }
  };

  // Bottom Tab variant - per NAVIGATION-FLOW-MOCKUP.html
  if (variant === 'bottomTab') {
    return (
      <button
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        disabled={item.disabled}
        aria-label={item.label}
        title={item.tooltip || item.label}
        role="tab"
        aria-selected={isActive}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          border: 'none',
          background: 'transparent',
          color: isActive ? 'var(--primary-700)' : 'var(--gray-600)',
          cursor: item.disabled ? 'not-allowed' : 'pointer',
          padding: '8px 0',
          position: 'relative',
          minWidth: '56px',
          transition: 'color 0.15s ease-out',
          opacity: item.disabled ? 0.5 : 1,
        }}
      >
        {/* Active Indicator - top bar */}
        {isActive && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '40px',
            height: '3px',
            background: 'linear-gradient(135deg, var(--primary-600) 0%, var(--primary-500) 100%)',
            borderRadius: '0 0 3px 3px',
          }} />
        )}

        {/* Icon — web-sync checklist A.2 (2026-08-15): `isCenter` renders a
            raised floating circle, mirroring the prototype's
            `.bottom-tab a.is-center` treatment (40px circle, coffee-700 bg,
            white icon, -14px raise, shadow) and mobile's TabConfig.isCenter. */}
        <div style={{ position: 'relative' }}>
          {item.isCenter ? (
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: isActive ? 'var(--primary-900)' : 'var(--primary-700)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: '-14px',
              // web-sync checklist A.17 (2026-08-15): was hardcoded
              // rgba(0,0,0,0.2), visibly darker than mobile/design's "md"
              // shadow alpha (0.06-0.08). Reference the token instead.
              boxShadow: 'var(--nqh-shadow-md)',
            }}>
              <IconComponent style={{ width: '20px', height: '20px', color: '#ffffff' }} />
            </div>
          ) : (
            <IconComponent
              style={{
                width: '24px',
                height: '24px',
                color: isActive ? 'var(--primary-700)' : 'var(--gray-400)',
              }}
            />
          )}
          {item.badge && item.badge > 0 && (
            <span style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              background: 'var(--error)',
              color: '#ffffff',
              fontSize: '10px',
              fontWeight: '700',
              borderRadius: '10px',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {item.badge > 99 ? '99+' : item.badge}
            </span>
          )}
        </div>

        {/* Label */}
        <span style={{
          fontSize: '11px',
          fontWeight: isActive ? '600' : '500',
          lineHeight: 1,
        }}>
          {item.label}
        </span>
      </button>
    );
  }

  // Sidebar variant - per NAVIGATION-FLOW-MOCKUP.html lines 370-413
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        disabled={item.disabled}
        aria-label={item.label}
        title={isCollapsed ? item.label : undefined}
        role="menuitem"
        aria-current={isActive ? 'page' : undefined}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          gap: '12px',
          padding: isActive ? '10px 12px 10px 8px' : '10px 12px',
          borderRadius: '8px',
          border: 'none',
          borderLeft: isActive ? '4px solid var(--primary-600)' : '4px solid transparent',
          // Per mockup lines 389-407: hover and active states
          background: isActive
            ? 'linear-gradient(90deg, var(--primary-100) 0%, var(--primary-50) 100%)'
            : isHovered && !item.disabled
              ? 'var(--primary-50)'
              : 'transparent',
          color: isActive
            ? 'var(--primary-800)'
            : isHovered && !item.disabled
              ? 'var(--primary-900)'
              : 'var(--gray-600)',
          fontSize: '0.875rem',
          fontWeight: '500',
          cursor: item.disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s',
          marginBottom: '4px',
          textAlign: 'left',
          opacity: item.disabled ? 0.5 : 1,
        }}
      >
        {/* Icon - per mockup lines 409-413 */}
        <div style={{ position: 'relative', flexShrink: 0, width: '20px', textAlign: 'center' }}>
          <IconComponent
            style={{
              width: '20px',
              height: '20px',
              color: isActive || (isHovered && !item.disabled)
                ? 'var(--primary-700)'
                : 'var(--gray-400)',
              transition: 'color 0.2s',
            }}
          />
          {item.badge && item.badge > 0 && (
            <span style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              background: 'var(--error)',
              color: '#ffffff',
              fontSize: '10px',
              fontWeight: '700',
              borderRadius: '8px',
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {item.badge > 9 ? '9+' : item.badge}
            </span>
          )}
        </div>

        {/* Label */}
        {!isCollapsed && (
          <span style={{ flex: 1 }}>
            {item.label}
          </span>
        )}

        {/* External link indicator */}
        {!isCollapsed && item.external && (
          <svg
            style={{ width: '12px', height: '12px', marginLeft: '8px', color: 'var(--gray-400)' }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        )}
      </button>

      {/* Tooltip for collapsed sidebar */}
      {isCollapsed && showTooltip && isHovered && (
        <div style={{
          position: 'absolute',
          left: '100%',
          top: '50%',
          transform: 'translateY(-50%)',
          marginLeft: '8px',
          padding: '4px 8px',
          background: 'var(--gray-900)',
          color: '#ffffff',
          fontSize: '0.75rem',
          borderRadius: '4px',
          whiteSpace: 'nowrap',
          zIndex: 50,
          pointerEvents: 'none',
        }}>
          {item.label}
          <div style={{
            position: 'absolute',
            right: '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            borderWidth: '4px',
            borderStyle: 'solid',
            borderColor: 'transparent var(--gray-900) transparent transparent',
          }} />
        </div>
      )}
    </div>
  );
};
